import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PHOTO_SLOT_TO_POSITION,
  getMissingGuidelinePhotoSlots,
  hasAllGuidelinePhotos,
  type PhotoGuidelineImageData,
  type PhotoGuidelineSlot,
} from '../components/photoGuidelines';
import { PhotoService } from '../services';
import { StudentPhotoUploadService } from '../services/StudentPhotoUploadService';
import { PHOTO_POSITION_TO_FORM_KEY } from '../types/photo';
import { prepareStudentPhotoFile } from '../utils/imageResize';
import { isAcceptedStudentPhotoFile } from '../utils/studentPhotoFile';

const MAX_SOURCE_FILE_SIZE = 15 * 1024 * 1024;

const EMPTY_PHOTOS: Record<PhotoGuidelineSlot, PhotoGuidelineImageData | null> = {
  front: null,
  back: null,
  rightSide: null,
  leftSide: null,
};

const buildImageData = (file: File, previewUrl: string): PhotoGuidelineImageData => ({
  previewUrl,
  fileName: file.name,
  fileSize: file.size,
  fileType: file.type.split('/')[1]?.toUpperCase() || 'JPEG',
  file,
});

const validateImageFile = (file: File) => {
  if (!isAcceptedStudentPhotoFile(file)) {
    throw new Error('Envie uma imagem em JPG, PNG, WEBP ou HEIC.');
  }

  if (file.size > MAX_SOURCE_FILE_SIZE) {
    throw new Error('A imagem deve ter no máximo 15 MB.');
  }
};

const mapBodyPhotosToGuidelineState = (
  bodyPhotos: Awaited<ReturnType<typeof PhotoService.getBodyPhotos>>,
  bustCache = false,
): Record<PhotoGuidelineSlot, PhotoGuidelineImageData | null> => {
  const nextPhotos = { ...EMPTY_PHOTOS };

  bodyPhotos.forEach((photo) => {
    const slot = PHOTO_POSITION_TO_FORM_KEY[photo.position] as PhotoGuidelineSlot;
    const previewUrl = bustCache
      ? `${photo.url}${photo.url.includes('?') ? '&' : '?'}t=${Date.now()}`
      : photo.url;

    nextPhotos[slot] = {
      previewUrl,
      fileName: `${photo.position}.jpg`,
      fileSize: 0,
      fileType: 'JPEG',
    };
  });

  return nextPhotos;
};

interface UseBodyPhotoUploadParams {
  studentId?: number | null;
  studentToken?: string;
}

interface UseBodyPhotoUploadResult {
  photos: Record<PhotoGuidelineSlot, PhotoGuidelineImageData | null>;
  isUploading: boolean;
  activeSlot: PhotoGuidelineSlot | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  cameraInputRef: React.RefObject<HTMLInputElement | null>;
  openFilePicker: (slot: PhotoGuidelineSlot, source?: 'gallery' | 'camera') => void;
  addPhotoFromFile: (slot: PhotoGuidelineSlot, file: File) => Promise<void>;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  removePhoto: (slot: PhotoGuidelineSlot) => void;
  uploadPhotos: () => Promise<void>;
  hasPhotos: boolean;
  hasAllPhotos: boolean;
}

export const useBodyPhotoUpload = ({
  studentId,
  studentToken,
}: UseBodyPhotoUploadParams): UseBodyPhotoUploadResult => {
  const [photos, setPhotos] = useState(EMPTY_PHOTOS);
  const [isUploading, setIsUploading] = useState(false);
  const [activeSlot, setActiveSlot] = useState<PhotoGuidelineSlot | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const previewUrlsRef = useRef<string[]>([]);
  const activeSlotRef = useRef<PhotoGuidelineSlot | null>(null);

  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const openFilePicker = useCallback((slot: PhotoGuidelineSlot, source: 'gallery' | 'camera' = 'gallery') => {
    activeSlotRef.current = slot;
    setActiveSlot(slot);
    const input = source === 'camera' ? cameraInputRef.current : fileInputRef.current;
    input?.click();
  }, []);

  const removePhoto = useCallback((slot: PhotoGuidelineSlot) => {
    setPhotos((current) => {
      const existing = current[slot];
      if (existing) {
        URL.revokeObjectURL(existing.previewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== existing.previewUrl);
      }

      return {
        ...current,
        [slot]: null,
      };
    });
  }, []);

  const addPhotoFromFile = useCallback(async (slot: PhotoGuidelineSlot, file: File) => {
    try {
      validateImageFile(file);
      const resizedFile = await prepareStudentPhotoFile(file);
      const previewUrl = URL.createObjectURL(resizedFile);
      previewUrlsRef.current.push(previewUrl);

      setPhotos((current) => {
        const existing = current[slot];
        if (existing) {
          URL.revokeObjectURL(existing.previewUrl);
          previewUrlsRef.current = previewUrlsRef.current.filter((url) => url !== existing.previewUrl);
        }

        return {
          ...current,
          [slot]: buildImageData(resizedFile, previewUrl),
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível processar a imagem.';
      window.alert(message);
    } finally {
      activeSlotRef.current = null;
      setActiveSlot(null);
    }
  }, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const slot = activeSlotRef.current;

    event.target.value = '';

    if (!file || !slot) {
      return;
    }

    await addPhotoFromFile(slot, file);
  }, [addPhotoFromFile]);

  const hasPhotos = Boolean(photos.front || photos.back || photos.rightSide || photos.leftSide);
  const hasAllPhotos = hasAllGuidelinePhotos(photos);

  const uploadPhotos = useCallback(async () => {
    if (!studentId) {
      throw new Error('Aluna não identificada para envio de fotos.');
    }

    if (!hasAllGuidelinePhotos(photos)) {
      const missingSlots = getMissingGuidelinePhotoSlots(photos);
      const titles: Record<PhotoGuidelineSlot, string> = {
        front: 'frente',
        back: 'costas',
        rightSide: 'lado direito',
        leftSide: 'lado esquerdo',
      };
      const missingLabels = missingSlots.map((slot) => titles[slot]).join(', ');
      throw new Error(`Faltam as fotos: ${missingLabels}.`);
    }

    setIsUploading(true);

    try {
      const entries = Object.entries(photos) as Array<[PhotoGuidelineSlot, PhotoGuidelineImageData | null]>;
      const photosToUpload = entries
        .filter((entry): entry is [PhotoGuidelineSlot, PhotoGuidelineImageData] => Boolean(entry[1]?.file))
        .map(([slot, image]) => ({
          position: PHOTO_SLOT_TO_POSITION[slot],
          file: image.file!,
        }));

      if (photosToUpload.length === 0) {
        return;
      }

      await PhotoService.uploadBodyPhotos(studentId, photosToUpload, { studentToken });

      if (studentToken) {
        await StudentPhotoUploadService.completePhotoUploadLink({
          studentId,
          studentToken,
        });
        return;
      }

      const refreshedPhotos = await PhotoService.getBodyPhotos(studentId);
      setPhotos(mapBodyPhotosToGuidelineState(refreshedPhotos, true));
    } finally {
      setIsUploading(false);
    }
  }, [photos, studentId, studentToken]);

  return {
    photos,
    isUploading,
    activeSlot,
    fileInputRef,
    cameraInputRef,
    openFilePicker,
    addPhotoFromFile,
    handleFileChange,
    removePhoto,
    uploadPhotos,
    hasPhotos,
    hasAllPhotos,
  };
};
